import { api } from "@/lib/api-client";
import type { ApiResponse } from "@/types/api";
import type { LoginResponse } from "@/types/auth";

export type MfaMethod = "totp" | "backup_code" | "sms" | "email" | "whatsapp" | "passkey";

// ─── WebAuthn / Passkey types ─────────────────────────────────────────────────

export interface WebAuthnCredentialItem {
  id: string;         // base64url-encoded credential ID
  display_name: string;
  created_at: string;
  last_used_at?: string;
}

export interface VerifyMfaLoginDTO {
  challenge_token: string;
  code: string;
  method: MfaMethod;
}

export interface ForgotPasswordDTO {
  tenant_id: string;
  email_or_phone: string;
}

export interface ResetPasswordDTO {
  token: string;
  new_password: string;
}

export interface MagicLinkRequestDTO {
  tenant_id: string;
  email_or_phone: string;
}

export interface TenantMatch {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
}

// Public (unauthenticated) + authenticated auth flows not covered by the
// login page itself: MFA challenge verification, password reset, magic
// links, and server-side logout.
export const authApi = {
  resolveTenant: (email: string) =>
    api
      .post<ApiResponse<{ tenants: TenantMatch[] }>>("/auth/resolve-tenant", { email })
      .then((r) => r.data.data.tenants),

  /**
   * Resolve a tenant by slug — used by subdomain and school-code (field)
   * resolution strategies. Returns the matching TenantMatch or throws on 404.
   * GET /auth/tenant/:slug
   */
  resolveTenantBySlug: (slug: string) =>
    api
      .get<ApiResponse<{ tenant: TenantMatch }>>(`/auth/tenant/${encodeURIComponent(slug)}`)
      .then((r) => r.data.data.tenant),

  verifyMfaLogin: (data: VerifyMfaLoginDTO) =>
    api
      .post<ApiResponse<LoginResponse>>("/auth/mfa/verify-login", data)
      .then((r) => r.data.data),

  forgotPassword: (data: ForgotPasswordDTO) =>
    api.post<ApiResponse<null>>("/auth/forgot-password", data).then((r) => r.data),

  resetPassword: (data: ResetPasswordDTO) =>
    api.post<ApiResponse<null>>("/auth/reset-password", data).then((r) => r.data),

  requestMagicLink: (data: MagicLinkRequestDTO) =>
    api.post<ApiResponse<null>>("/auth/magic-link", data).then((r) => r.data),

  verifyMagicLink: (token: string) =>
    api
      .post<ApiResponse<LoginResponse>>("/auth/magic-link/verify", { token })
      .then((r) => r.data.data),

  // No body needed — the refresh_token HttpOnly cookie is sent automatically
  // by the browser (withCredentials: true is set on the axios instance).
  // The server reads the cookie and clears all auth cookies on success.
  logout: () =>
    api.post<ApiResponse<null>>("/auth/logout").then((r) => r.data),

  logoutAll: () => api.post<ApiResponse<null>>("/auth/logout/all").then((r) => r.data),

  // ─── WebAuthn / Passkey ────────────────────────────────────────────────────

  /** Begin passkey registration — returns raw CredentialCreation options JSON. */
  webAuthnBeginRegister: () =>
    api.post<unknown>("/auth/webauthn/register/begin").then((r) => r.data),

  /** Complete passkey registration. displayName labels the key for the user. */
  webAuthnCompleteRegister: (credentialResponse: unknown, displayName: string) =>
    api
      .post<ApiResponse<{ credential_id: string; display_name: string }>>(
        `/auth/webauthn/register/complete?display_name=${encodeURIComponent(displayName)}`,
        credentialResponse,
      )
      .then((r) => r.data.data),

  /** List this user's registered passkeys. */
  webAuthnListCredentials: () =>
    api
      .get<ApiResponse<WebAuthnCredentialItem[]>>("/auth/webauthn/credentials")
      .then((r) => r.data.data),

  /** Delete a passkey by its base64url-encoded credential ID. */
  webAuthnDeleteCredential: (credentialId: string) =>
    api
      .delete<ApiResponse<null>>(`/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`)
      .then((r) => r.data),

  /** Begin passkey login (MFA step-up). Returns raw CredentialAssertion options JSON. */
  webAuthnBeginLogin: (mfaChallengeToken: string) =>
    api
      .get<unknown>(`/auth/webauthn/login/begin?mfa_challenge_token=${encodeURIComponent(mfaChallengeToken)}`)
      .then((r) => r.data),

  /** Complete passkey login. Returns the same shape as a successful /auth/login. */
  webAuthnCompleteLogin: (mfaChallengeToken: string, assertionResponse: unknown) =>
    api
      .post<ApiResponse<{ user: import("@/types/auth").User }>>(
        `/auth/webauthn/login/complete?mfa_challenge_token=${encodeURIComponent(mfaChallengeToken)}`,
        assertionResponse,
      )
      .then((r) => r.data.data),
};
